import { isUndefined } from 'lodash';
import { useEffect } from 'react';
import { UseFormSetValue, useWatch } from 'react-hook-form';
import { usePrevious } from 'react-use';

import { useFormContext } from '@/components/react-hook-form';
import {
  defaultPolicy,
  INSURANCE_POLICIES_DETAILS_SUBFORM,
  InsurancePolicyDetails,
} from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.types';
import { mapInsurancePolicy } from '@/modules/entities/InsurancePolicyDetailsSubform/InsurancePolicyDetailsSubform.utils';

import { HypotheticalIlitModalData_InsurancePolicyFragment } from '../graphql/HypotheticalILITModalData.generated';
import {
  HypotheticalILITModalContextShape,
  useHypotheticalILITModalContext,
} from '../HypotheticalILITModal.context';
import {
  HypotheticalILITFormModalShape,
  HypotheticalILITFormModalShapePaths,
  HypotheticalILITInsurancePolicyType,
} from '../HypotheticalILITModal.types';

const FORM_PATH =
  `${INSURANCE_POLICIES_DETAILS_SUBFORM}.policies.0` as const satisfies HypotheticalILITFormModalShapePaths;

const getFieldNameForUpdate = (fieldName: keyof InsurancePolicyDetails) => {
  return `${FORM_PATH}.${fieldName}` as const satisfies HypotheticalILITFormModalShapePaths;
};

export function useUpdateInsuranceFormAfterSelection() {
  const { existingPolicies, initialHypotheticalInsurance } =
    useHypotheticalILITModalContext();
  const { control, setValue } =
    useFormContext<HypotheticalILITFormModalShape>();

  const [policyType, existingInsurancePolicyId] = useWatch({
    control,
    name: ['_hypotheticalInsurancePolicyType', 'existingInsurancePolicyId'],
  });

  const previousPolicyType = usePrevious(policyType);
  const previousExistingInsurancePolicyId = usePrevious(
    existingInsurancePolicyId
  );

  // repopulate the form when the existing insurance policy ID changes
  useEffect(() => {
    private_updateInsuranceFormHookBody({
      existingInsurancePolicyId,
      existingPolicies,
      policyType,
      previousExistingInsurancePolicyId,
      previousPolicyType,
      setValue,
      initialCreatedInsurancePolicy:
        initialHypotheticalInsurance?.createdInsurancePolicy || null,
    });
  }, [
    existingInsurancePolicyId,
    existingPolicies,
    initialHypotheticalInsurance?.createdInsurancePolicy,
    policyType,
    previousExistingInsurancePolicyId,
    previousPolicyType,
    setValue,
  ]);
}

interface HookBodyInput {
  existingInsurancePolicyId: string;
  existingPolicies: HypotheticalILITModalContextShape['existingPolicies'];
  policyType: HypotheticalILITInsurancePolicyType | null;
  previousExistingInsurancePolicyId: string | undefined;
  previousPolicyType: HypotheticalILITInsurancePolicyType | null | undefined;
  setValue: UseFormSetValue<HypotheticalILITFormModalShape>;
  initialCreatedInsurancePolicy: HypotheticalIlitModalData_InsurancePolicyFragment | null;
}

export function private_updateInsuranceFormHookBody({
  existingInsurancePolicyId,
  existingPolicies,
  policyType,
  previousExistingInsurancePolicyId,
  previousPolicyType,
  setValue,
  initialCreatedInsurancePolicy,
}: HookBodyInput) {
  // do not repopulate the form on first render
  if (isUndefined(previousPolicyType)) return;
  if (isUndefined(previousExistingInsurancePolicyId)) return;

  // do not repopulate the form if on new and not changing
  if (
    policyType === HypotheticalILITInsurancePolicyType.New &&
    previousPolicyType === HypotheticalILITInsurancePolicyType.New
  ) {
    return;
  }

  // do not repopulate the form if on existing and not changing
  if (
    policyType === HypotheticalILITInsurancePolicyType.Existing &&
    previousPolicyType === HypotheticalILITInsurancePolicyType.Existing &&
    previousExistingInsurancePolicyId === existingInsurancePolicyId
  ) {
    return;
  }

  // if switching to new, reset the form
  if (
    policyType === HypotheticalILITInsurancePolicyType.New &&
    previousPolicyType === HypotheticalILITInsurancePolicyType.Existing
  ) {
    if (initialCreatedInsurancePolicy) {
      const updatedFormValue = mapInsurancePolicy(
        initialCreatedInsurancePolicy
      );
      setValue(FORM_PATH, { ...updatedFormValue });
    } else {
      setValue(FORM_PATH, {
        ...defaultPolicy,
        policyHolderIDs: ['', ''],
      });
    }
    return;
  }

  // if switching existing policies, repopulate the form
  const existingPolicy = existingPolicies.find(
    (policy) => policy.id === existingInsurancePolicyId
  );
  if (
    policyType === HypotheticalILITInsurancePolicyType.Existing &&
    existingPolicy
  ) {
    const updatedFormValue = mapInsurancePolicy(existingPolicy);
    setValue(FORM_PATH, { ...updatedFormValue });
    // these three fields are decimalJS values, and the form was not consistently
    // displaying the latest values based on the above setValue call
    // so we re-set them separately
    setValue(
      getFieldNameForUpdate('deathBenefitAmount'),
      updatedFormValue.deathBenefitAmount
    );
    setValue(
      getFieldNameForUpdate('premiumAmount'),
      updatedFormValue.premiumAmount
    );
    setValue(getFieldNameForUpdate('cashValue'), updatedFormValue.cashValue);
    setValue(
      getFieldNameForUpdate('loanBalanceOutstanding'),
      updatedFormValue.loanBalanceOutstanding
    );
    return;
  }
}
